import React, { ForwardRefRenderFunction, forwardRef, useMemo } from "react";
//#region types
import { SupplierFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { SupplierRuleFormContainerProps } from "models/propTypes/supplierPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormFieldConstants } from "utils/constants";
//#endregion
//#region  components
import SupplierRuleForm from "./ruleForm";
import { useSupplierService } from "serviceHooks";
import { useBookingTreeItems } from "components/incomingInvoice";
//#endregion
import * as Yup from "yup";

const SupplierRuleFormContainer: ForwardRefRenderFunction<FormHandles, SupplierRuleFormContainerProps> = (
  props,
  ref
) => {
  const { searchBookingTreeItemsAutoComplete: searchBookingTreeItems } = useBookingTreeItems();
  const supplierService = useSupplierService();
  const {
    supplierId,
    id,
    active,
    bookingAccountId,
    bookingAccountName,
    rulePeriod,
    maxAmount,
    minAmount,
    fromDate,
    toDate,
    dimensions,
  } = props;

  const dimensionAmount = 1; //positive amount for the dialog to work

  const initialValues = useMemo(
    (): SupplierFormTypes.Rule => ({
      id: id,
      active: active ?? true,
      bookingAccountId: bookingAccountId,
      bookingAccountName: bookingAccountName,
      rulePeriod: rulePeriod ?? WintSuperkollAPIModel.IncomingInvoiceRulePeriod.Month,
      rulePeriodName: supplierService.getRulePeriodText(
        rulePeriod ?? WintSuperkollAPIModel.IncomingInvoiceRulePeriod.Month
      ),
      amountHighest: maxAmount,
      amountLowest: minAmount,
      dateFrom: fromDate,
      dateTo: toDate,
      dimensions: dimensions?.map((value) => ({
        dimensionId: value.dimensionId,
        amount: dimensionAmount,
      })),
    }),
    [props]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object<SupplierFormTypes.Rule>({
        id: Yup.number(),
        active: Yup.boolean(),
        bookingAccountId: Yup.number().required(TranslationKeys.Validations.Required),
        bookingAccountName: Yup.string().required(TranslationKeys.Validations.Required),
        rulePeriod: Yup.number().required(TranslationKeys.Validations.Required),
        rulePeriodName: Yup.string().required(TranslationKeys.Validations.Required),
        amountHighest: Yup.number()
          .required(TranslationKeys.Validations.Required)
          .when(
            FormFieldConstants.Supplier.Rule.AmountLowest,
            ([minAmount], schema: Yup.NumberSchema) =>
              (minAmount && schema.min(minAmount, TranslationKeys.Validations.MaxAmountLessThanMinAmount)) || schema
          ),
        amountLowest: Yup.number().required(TranslationKeys.Validations.Required),
        dateFrom: Yup.date().typeError(TranslationKeys.Validations.InvalidDate),
        dateTo: Yup.date()
          .typeError(TranslationKeys.Validations.InvalidDate)
          .when(
            FormFieldConstants.Supplier.Rule.DateFrom,
            ([dateFrom], schema: Yup.DateSchema) =>
              (dateFrom && schema.min(dateFrom, TranslationKeys.Validations.ToDateLessThanFromDate)) || schema
          ),
        dimensions: Yup.array(),
      }),
    []
  );

  const handleSubmit = async (values: SupplierFormTypes.Rule): Promise<WintSuperkollAPIModel.IncomingInvoiceRule> => {
    const rulePost: WintSuperkollAPIModel.IncomingInvoiceRulePut = {
      supplierId: supplierId,
      active: values.active,
      bookingAccountId: values.bookingAccountId ?? 0,
      rulePeriod: values.rulePeriod ?? WintSuperkollAPIModel.IncomingInvoiceRulePeriod.Month,
      maxAmount: values.amountHighest ?? 0,
      minAmount: values.amountLowest ?? 0,
      fromDate: values.dateFrom,
      toDate: values.dateTo,
      dimensions: values.dimensions,
    };

    let ruleId = values.id;
    if (values.id === undefined) ruleId = await supplierService.createRule(rulePost);
    else await supplierService.updateRule(values.id, rulePost);

    return { ...rulePost, id: ruleId ?? 0, bookingAccountName: values.bookingAccountName };
  };

  return (
    <SupplierRuleForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      handleSubmit={handleSubmit}
      onFormValidationChange={props.onFormValidationChange}
      searchBookingTreeHandler={searchBookingTreeItems}
      dimensionAmount={dimensionAmount}
    />
  );
};

export default forwardRef(SupplierRuleFormContainer);
